import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Globe, Users2, User, X, Search } from "lucide-react";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: "dashboard";
  /** The ID of the dashboard_snapshot */
  resourceId: string;
  resourceTitle: string;
}

interface Group {
  id: string;
  name: string;
  member_count: number;
}

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface ShareEntry {
  id: string;
  share_type: string;
  group_id: string | null;
  profile_id: string | null;
}

const ShareDialog = ({ open, onOpenChange, resourceType, resourceId, resourceTitle }: ShareDialogProps) => {
  const { profile: currentProfile } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Current sharing state
  const [isPublic, setIsPublic] = useState(false);
  const [sharedGroupIds, setSharedGroupIds] = useState<Set<string>>(new Set());
  const [sharedProfileIds, setSharedProfileIds] = useState<Set<string>>(new Set());
  const [existingShares, setExistingShares] = useState<ShareEntry[]>([]);

  // Available options
  const [groups, setGroups] = useState<Group[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);

  // Search
  const [groupSearch, setGroupSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");

  // Active tab
  const [activeSection, setActiveSection] = useState<"public" | "groups" | "employees">("groups");

  const shareTable = "dashboard_shares";
  const resourceIdColumn = "dashboard_id";

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, resourceId]);

  const loadData = async () => {
    setLoading(true);
    setGroupSearch("");
    setEmployeeSearch("");

    if (!currentProfile?.client_id) {
      setLoading(false);
      return;
    }

    // Fetch existing shares, groups, and profiles in parallel
    const [sharesResult, groupsResult, profilesResult] = await Promise.all([
      supabase
        .from(shareTable as any)
        .select("id, share_type, group_id, profile_id")
        .eq(resourceIdColumn, resourceId),
      supabase
        .from("groups" as any)
        .select("id, name")
        .eq("client_id", currentProfile.client_id)
        .order("name"),
      supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url")
        .eq("client_id", currentProfile.client_id)
        .eq("enabled", true)
        .order("full_name"),
    ]);

    // Process existing shares
    const shares = (sharesResult.data || []) as any[];
    setExistingShares(shares);
    setIsPublic(shares.some((s: any) => s.share_type === "public"));
    setSharedGroupIds(new Set(shares.filter((s: any) => s.share_type === "group").map((s: any) => s.group_id)));
    setSharedProfileIds(new Set(shares.filter((s: any) => s.share_type === "employee").map((s: any) => s.profile_id)));

    // Process groups with member counts
    const groupsData = (groupsResult.data || []) as any[];
    const groupIds = groupsData.map((g: any) => g.id);
    let memberCounts: Record<string, number> = {};
    if (groupIds.length > 0) {
      const { data: memberData } = await supabase
        .from("group_members" as any)
        .select("group_id")
        .in("group_id", groupIds);
      if (memberData) {
        (memberData as any[]).forEach((m: any) => {
          memberCounts[m.group_id] = (memberCounts[m.group_id] || 0) + 1;
        });
      }
    }
    setGroups(groupsData.map((g: any) => ({ ...g, member_count: memberCounts[g.id] || 0 })));

    // Process profiles (exclude current user)
    setProfiles(((profilesResult.data || []) as Profile[]).filter((p) => p.id !== currentProfile?.id));

    setLoading(false);
  };

  const handleSave = async () => {
    if (!currentProfile?.id || !currentProfile.client_id) return;
    setSaving(true);

    try {
      // Delete all existing shares for this resource
      await supabase
        .from(shareTable as any)
        .delete()
        .eq(resourceIdColumn, resourceId);

      const inserts: any[] = [];

      // Add public share if enabled
      if (isPublic) {
        inserts.push({
          [resourceIdColumn]: resourceId,
          shared_by: currentProfile.id,
          share_type: "public",
          client_id: currentProfile.client_id,
        });
      }

      // Add group shares
      for (const groupId of sharedGroupIds) {
        inserts.push({
          [resourceIdColumn]: resourceId,
          shared_by: currentProfile.id,
          share_type: "group",
          group_id: groupId,
          client_id: currentProfile.client_id,
        });
      }

      // Add employee shares
      for (const profileId of sharedProfileIds) {
        inserts.push({
          [resourceIdColumn]: resourceId,
          shared_by: currentProfile.id,
          share_type: "employee",
          profile_id: profileId,
          client_id: currentProfile.client_id,
        });
      }

      if (inserts.length > 0) {
        const { error } = await supabase.from(shareTable as any).insert(inserts);
        if (error) throw error;
      }

      const parts: string[] = [];
      if (isPublic) parts.push("public");
      if (sharedGroupIds.size > 0) parts.push(`${sharedGroupIds.size} group${sharedGroupIds.size !== 1 ? "s" : ""}`);
      if (sharedProfileIds.size > 0) parts.push(`${sharedProfileIds.size} employee${sharedProfileIds.size !== 1 ? "s" : ""}`);

      toast({
        title: "Sharing updated",
        description: parts.length > 0
          ? `Shared with ${parts.join(", ")}`
          : "All sharing removed",
      });
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const toggleGroup = (groupId: string) => {
    setSharedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleEmployee = (profileId: string) => {
    setSharedProfileIds((prev) => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const filteredGroups = groups.filter((g) => {
    if (!groupSearch) return true;
    return g.name.toLowerCase().includes(groupSearch.toLowerCase());
  });

  const filteredProfiles = profiles.filter((p) => {
    if (!employeeSearch) return true;
    const search = employeeSearch.toLowerCase();
    return (
      (p.full_name && p.full_name.toLowerCase().includes(search)) ||
      (p.email && p.email.toLowerCase().includes(search))
    );
  });

  const totalShares = (isPublic ? 1 : 0) + sharedGroupIds.size + sharedProfileIds.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Share {resourceType === "dashboard" ? "Dashboard" : "Report"}</DialogTitle>
          <DialogDescription className="truncate">
            {resourceTitle}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Summary badges */}
            {totalShares > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {isPublic && (
                  <Badge variant="secondary" className="gap-1">
                    <Globe className="h-3 w-3" /> Public
                    <button onClick={() => setIsPublic(false)} className="ml-0.5 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {Array.from(sharedGroupIds).map((gid) => {
                  const group = groups.find((g) => g.id === gid);
                  return (
                    <Badge key={gid} variant="secondary" className="gap-1">
                      <Users2 className="h-3 w-3" /> {group?.name || "Group"}
                      <button onClick={() => toggleGroup(gid)} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
                {Array.from(sharedProfileIds).map((pid) => {
                  const profile = profiles.find((p) => p.id === pid);
                  return (
                    <Badge key={pid} variant="secondary" className="gap-1">
                      <User className="h-3 w-3" /> {profile?.full_name || "Employee"}
                      <button onClick={() => toggleEmployee(pid)} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Public toggle */}
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Public for all users</p>
                  <p className="text-xs text-muted-foreground">Everyone in your organization can view</p>
                </div>
              </div>
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
            </div>

            {/* Section tabs */}
            <div className="flex gap-1 border-b border-border">
              <button
                onClick={() => setActiveSection("groups")}
                className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                  activeSection === "groups" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Groups {sharedGroupIds.size > 0 && `(${sharedGroupIds.size})`}
                {activeSection === "groups" && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary rounded-full" />
                )}
              </button>
              <button
                onClick={() => setActiveSection("employees")}
                className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                  activeSection === "employees" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Employees {sharedProfileIds.size > 0 && `(${sharedProfileIds.size})`}
                {activeSection === "employees" && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            </div>

            {/* Groups section */}
            {activeSection === "groups" && (
              <div className="flex flex-col gap-2 flex-1 min-h-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex-1 overflow-y-auto border rounded-md min-h-0 max-h-[30vh]">
                  {filteredGroups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
                      <Users2 className="h-8 w-8 mb-2" />
                      {groups.length === 0
                        ? "No groups created yet. Create groups in Admin > Group Management."
                        : "No groups match your search."}
                    </div>
                  ) : (
                    filteredGroups.map((g) => (
                      <label
                        key={g.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent cursor-pointer border-b last:border-0"
                      >
                        <Checkbox
                          checked={sharedGroupIds.has(g.id)}
                          onCheckedChange={() => toggleGroup(g.id)}
                        />
                        <Users2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-medium">{g.name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {g.member_count} member{g.member_count !== 1 ? "s" : ""}
                        </Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Employees section */}
            {activeSection === "employees" && (
              <div className="flex flex-col gap-2 flex-1 min-h-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex-1 overflow-y-auto border rounded-md min-h-0 max-h-[30vh]">
                  {filteredProfiles.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      No employees found
                    </div>
                  ) : (
                    filteredProfiles.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent cursor-pointer border-b last:border-0"
                      >
                        <Checkbox
                          checked={sharedProfileIds.has(p.id)}
                          onCheckedChange={() => toggleEmployee(p.id)}
                        />
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={p.avatar_url ?? undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(p.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">
                            {p.full_name || "Unnamed"}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {p.email || "—"}
                          </span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
